import React, { useEffect, useState } from 'react';

import { 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    Button, 
    Box, 
    CircularProgress,
    Chip,
    Typography,
    Stack
} from '@mui/material';

import { useCompanyService } from 'store/services/company.service';

const HeaderInfoModal = ({ open, onClose, header, companyId, onApplyTags, selectedTagIds = [] }) => {
    const [tags, setTags] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedTags, setSelectedTags] = useState([]);

    const { getInventoryTags } = useCompanyService();

    const handleTagClick = (tag) => {
        setSelectedTags(prev => {
            if (prev.includes(tag)) {
                return prev.filter(t => t !== tag);
            } else {
                return [...prev, tag];
            }
        });
    };

    const handleApply = () => {
        onApplyTags(selectedTags);
        onClose();
    };

    useEffect(() => {
        const fetchTags = async () => {
            if (!open || !companyId) return;
            
            setLoading(true);
            setError(null);
            
            try {
                const response = await getInventoryTags(companyId);
                const allTags = response.data || [];
                setTags(allTags);
                
       
                if (selectedTagIds.length > 0) {
                    const selected = allTags.filter(tag => 
                        selectedTagIds.includes(tag.id) || selectedTagIds.some(id => id.id === tag.id)
                    );
                    setSelectedTags(selected);
                } else {
                    setSelectedTags([]);
                }
            } catch (err) {
                console.error('Error fetching tags:', err);
                setError('Failed to load tags. Please try again.');
                setSelectedTags([]);
            } finally {
                setLoading(false);
            }
        };

        fetchTags();
    }, [open, companyId, selectedTagIds]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Filter by Tags</DialogTitle>
            <DialogContent>
                <Box p={2}>
                    {loading ? (
                        <Box display="flex" justifyContent="center" p={2}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : error ? (
                        <Typography color="error">{error}</Typography>
                    ) : tags.length > 0 ? (
                        <Stack spacing={2}>
                            <Box display="flex" flexWrap="wrap" gap={1}>
                                {tags.map((tag, index) => {
                                    const isSelected = selectedTags.includes(tag);
                                    return (
                                        <Chip 
                                            key={index}
                                            label={tag.name || `Tag ${index + 1}`}
                                            variant={isSelected ? 'filled' : 'outlined'}
                                            color={isSelected ? 'primary' : 'default'}
                                            onClick={() => handleTagClick(tag)}
                                            sx={{ 
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    opacity: 0.8,
                                                },
                                            }}
                                        />
                                    );
                                })}
                            </Box>
                            <Typography variant="caption" color="textSecondary">
                                {selectedTags.length} tag(s) selected
                            </Typography>
                        </Stack>
                    ) : (
                        <Typography>No tags available</Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit">
                    Cancel
                </Button>
                <Button 
                    onClick={handleApply} 
                    color="primary"
                    variant="contained"
                >
                    Apply
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default HeaderInfoModal;
